<script setup lang="ts">
    import ThousandSeparatorInput from '@/components/ThousandSeparatorInput.vue';
    import type { CellContext, Row, Table } from '@tanstack/vue-table';
    import type { VatSubmission } from '@/types';
    import { computed, ref, watch } from 'vue';
    import { useI18n } from 'vue-i18n';
    import { useVatStore } from '@/stores/vat';
    import { formatCurrency } from '@/utils/helpers';

    const props = defineProps<{
        row: Row<VatSubmission>;
        table: Table<VatSubmission>;
        columnId: keyof VatSubmission;
    }>();

    const { t } = useI18n();

    const vatStore = useVatStore();

    const val = (n: number | null | undefined) => n ?? 0;

    const calculatedValue = computed(
        () =>
            val(props.row.original.sales_vat) -
            val(props.row.original.purchase_vat) +
            val(props.row.original.vat_on_purchase_of_goods_abroad) +
            val(props.row.original.vat_on_purchase_of_services_abroad) -
            val(props.row.original.oil_and_bottled_gas_tax) -
            val(props.row.original.electricity_tax) -
            val(props.row.original.natural_gas_and_town_gas_tax) -
            val(props.row.original.coal_tax) -
            val(props.row.original.co2_tax) -
            val(props.row.original.water_tax),
    );

    watch(calculatedValue, (newVal) => {
        props.table.options.meta?.updateData?.(props.row, props.columnId, calculatedValue);
    });
</script>

<template>
    <div class="flex flex-col gap-1">
        <div class="relative flex items-center">
            <ThousandSeparatorInput
                v-model="calculatedValue"
                with-decimals
                :placeholder="t('vat.group_headers.vat_total')"
                class="min-w-52 pr-8"
                :disabled="true"
            />
            <span class="pointer-events-none absolute right-2">Kr.</span>
        </div>
        <span
            v-if="vatStore.showPreviousSubmission && row.original.previous_submission"
            class="text-faded-foreground flex items-center justify-between gap-1 text-[10px]"
        >
            <span>{{
                formatCurrency(row.original.previous_submission[props.columnId] as number)
            }}</span>
            <span>{{ t('vat.previous_submission') }}</span>
        </span>
    </div>
</template>
